import { createSlice } from '@reduxjs/toolkit';

import { SourceType } from '@/types/document';
import { Language } from '@/types/i18n';

import {
  hideFileViewer,
  hideReviewPersonalInfo,
  hideUpdatePersonalInfo,
  hideUserInfo,
  initiateProcessStep,
  resetProcessStep,
  setFileViewerScrolledToBottom,
  setUserSelectedLanguage,
  showFileViewer,
  showReviewPersonalInfo,
  showUpdatePersonalInfo,
  showUserInfo,
  updateProcessStep,
} from './actions';

interface UIState {
  fileViewer?: {
    src: string;
    type: SourceType;
    isScrolledToBottom?: boolean;
  };
  isShowingUserInfo?: boolean;
  isShowingReviewPersonalInfo?: boolean;
  isShowingUpdatePersonalInfo?: boolean;
  userSelectedLanguage?: Language;
  processStepMap: Record<string, { current: number; total: number }>;
}

export const INITIAL_UI_STATE: UIState = {
  isShowingUserInfo: false,
  isShowingReviewPersonalInfo: false,
  isShowingUpdatePersonalInfo: false,
  userSelectedLanguage: undefined,
  processStepMap: {},
};

export const uiSlice = createSlice({
  name: 'ui',
  initialState: INITIAL_UI_STATE,
  reducers: {},
  extraReducers(builder) {
    builder.addCase(showFileViewer, (state, { payload }) => {
      state.fileViewer = payload;
    });
    builder.addCase(
      setFileViewerScrolledToBottom,
      (state, { payload: isScrolledToBottom }) => {
        if (state.fileViewer) {
          state.fileViewer.isScrolledToBottom = isScrolledToBottom;
        }
      },
    );
    builder.addCase(hideFileViewer, state => {
      state.fileViewer = undefined;
    });
    builder.addCase(showUserInfo, state => {
      state.isShowingUserInfo = true;
    });
    builder.addCase(hideUserInfo, state => {
      state.isShowingUserInfo = false;
    });

    builder.addCase(showReviewPersonalInfo, state => {
      state.isShowingReviewPersonalInfo = true;
    });
    builder.addCase(hideReviewPersonalInfo, state => {
      state.isShowingReviewPersonalInfo = false;
    });
    builder.addCase(showUpdatePersonalInfo, state => {
      state.isShowingReviewPersonalInfo = false;
      state.isShowingUpdatePersonalInfo = true;
    });
    builder.addCase(hideUpdatePersonalInfo, state => {
      state.isShowingUpdatePersonalInfo = false;
    });
    builder.addCase(setUserSelectedLanguage, (state, { payload }) => {
      state.userSelectedLanguage = payload;
    });
    builder.addCase(initiateProcessStep, (state, { payload }) => {
      const uniqueName = payload.uniqueName;
      if (!state.processStepMap[uniqueName]) {
        state.processStepMap[uniqueName] = {
          current: payload.current || 0,
          total: payload.total,
        };
      }
    });
    builder.addCase(updateProcessStep, (state, { payload }) => {
      const uniqueName = payload.uniqueName;
      if (state.processStepMap[uniqueName]) {
        state.processStepMap[uniqueName].current = payload.current;
      }
    });
    builder.addCase(resetProcessStep, (state, { payload }) => {
      const uniqueName = payload.uniqueName;
      delete state.processStepMap[uniqueName];
    });
  },
});

export const uiReducer = uiSlice.reducer;
